/**
 * degreeDaysNarrativeService.ts
 *
 * Deterministic smart narrative for HDD65/CDD65 (no LLM).
 */

import type { DegreeDayAnnualRow, DegreeDayStatewideRow } from './degreeDaysDataService';

function round0(n: number): number {
  return Math.round(n);
}

function pctDiff(value: number, benchmark: number): number | null {
  if (Math.abs(benchmark) < 5) return null;
  return Math.round(((value - benchmark) / benchmark) * 100);
}

function comparePhrase(value: number, benchmark: number, label: string): string {
  const diff = value - benchmark;
  const pct = pctDiff(value, benchmark);
  if (pct == null) {
    if (Math.abs(diff) < 10) {
      return `${label} was near the California incorporated-city average`;
    }
    return diff > 0
      ? `${label} was higher than the California incorporated-city average`
      : `${label} was lower than the California incorporated-city average`;
  }
  const dir = diff > 0 ? 'higher' : 'lower';
  return `${label} was ${Math.abs(pct)}% ${dir} than the California incorporated-city average`;
}

export interface DegreeDayNarrativeInput {
  locationLabel: string;
  year: number;
  yearSubstituted: boolean;
  city: DegreeDayAnnualRow | null;
  statewide: DegreeDayStatewideRow | null;
}

export function generateDegreeDayOverviewNarrative(input: DegreeDayNarrativeInput): string {
  const { locationLabel, year, yearSubstituted, city, statewide } = input;
  if (!city) {
    return `No heating or cooling degree day data are available for ${locationLabel} in ${year}.`;
  }

  const hdd = round0(city.hdd65_actual);
  const cdd = round0(city.cdd65_actual);
  let text = `In ${year}, ${locationLabel} recorded ${hdd.toLocaleString()} heating degree days and ${cdd.toLocaleString()} cooling degree days`;

  if (statewide) {
    const hddCmp = comparePhrase(city.hdd65_actual, statewide.mean_hdd65_actual, 'Heating demand');
    const cddCmp = comparePhrase(city.cdd65_actual, statewide.mean_cdd65_actual, 'Cooling demand');
    text += `. ${hddCmp}, while ${cddCmp.charAt(0).toLowerCase() + cddCmp.slice(1)}`;
  }

  const hddAnom = round0(city.hdd65_anomaly);
  const cddAnom = round0(city.cdd65_anomaly);
  const hddDir = hddAnom < 0 ? 'fewer' : hddAnom > 0 ? 'more' : 'the same number of';
  const cddDir = cddAnom < 0 ? 'fewer' : cddAnom > 0 ? 'more' : 'the same number of';

  if (hddAnom === 0 && cddAnom === 0) {
    text += `. Compared with ${locationLabel}'s 1991–2020 climate normal, heating and cooling degree days were both near normal.`;
  } else {
    text += `. Compared with ${locationLabel}'s 1991–2020 climate normal, the year had `;
    const parts: string[] = [];
    if (hddAnom !== 0) {
      parts.push(`${Math.abs(hddAnom).toLocaleString()} ${hddDir} heating degree days`);
    }
    if (cddAnom !== 0) {
      parts.push(`${Math.abs(cddAnom).toLocaleString()} ${cddDir} cooling degree days`);
    }
    text += parts.join(' and ');

    if (cddAnom > 0 && hddAnom < 0) {
      text += ', indicating a warmer-than-normal balance between space-heating and cooling conditions';
    } else if (cddAnom < 0 && hddAnom > 0) {
      text += ', indicating a cooler-than-normal balance between space-heating and cooling conditions';
    } else if (cddAnom > hddAnom && cddAnom > 0) {
      text += ', with relatively greater cooling-degree-day accumulation';
    } else if (hddAnom > cddAnom && hddAnom > 0) {
      text += ', with relatively greater heating-degree-day accumulation';
    }
    text += '.';
  }

  if (yearSubstituted) {
    text += ` Climate data for ${year} use the latest available PRISM year (${year}) because the selected inventory year is outside the 2000–2024 coverage period.`;
  }

  return text;
}
